// packages/shared/src/yebo-only-config.ts
//
// #808 (#541-b) — THE R22 LINE, as one list.
//
// RULING R22 (Rob, 2026-09-22, on #541): **Yebo-only = payment provider and
// deposit rules, and nothing else.** Meal windows, booking duration, tables and
// sections INCLUDING the online flags, pacing caps, Customer Booking
// Availability and outbound-messaging setup are VENUE-OWNED — an owner or a
// manager changes them unaided. R21: no new role is introduced; the line is
// drawn per SETTING, not per person.
//
// WHY THESE AND NOT OTHERS. Everything here either selects the gateway that
// will charge a guest's card, or decides how much is taken from it. A wrong
// value does not degrade a service day — it takes real money from a real guest
// (or fails to, after they have already committed to a booking), and the
// remedy is a refund, not an edit. Everything else in Settings is recoverable
// by the venue in the next save.
//
// WHY A SHARED MODULE. Three surfaces need the same list and must not drift:
//   • the server guard on the config write channels (`guardYeboOnlyKeys` in
//     packages/middleware/src/routes/config-guards.ts) — the trust boundary,
//   • the dashboard, which renders these fields read-only with a "Managed by
//     Yebo" note rather than hiding them (a venue must still be able to SEE
//     what it charges),
//   • the drift test that pins every key against `LocationConfig`
//     (packages/middleware/src/__tests__/yebo-only-config-keys.test.ts — the
//     type is declared in the middleware package, so the check lives there).
//
// NOT HERE, DELIBERATELY:
//   • `requirePayment` / `depositCheckout` — not LocationConfig attributes;
//     they are derived projections in the PUBLIC widget config.
//   • `widgetPaymentProvider`, `widgetStripePk`, `depositAmountPerPerson`,
//     `depositKidsAmountPerPerson`, `depositOverrides` — #786 removed these as
//     DEAD keys (no reader anywhere). Naming a dead key in a guard is noise.
//   • per-event deposits and ticket prices inside `events[]` — a venue prices
//     its own Valentine's night, and `events` is venue-owned whole-array LWW.
//   • the PayFast merchant credentials — those never travel through a config
//     write at all; they live in Secrets Manager behind /api/payfast/connect.

/**
 * The config attributes only a Yebo admin may write (R22). Every entry is a
 * top-level `LocationConfig` key — see the drift test named above.
 */
export const YEBO_ONLY_CONFIG_KEYS = Object.freeze([
  /** Which gateway charges the guest (stripe | paystack | square | payfast | none | ghl). */
  'paymentProvider',
  /** The currency every deposit/payment amount is denominated in. */
  'paymentCurrency',
  /** Legacy flat Stripe-Elements amount (cents) taken at booking. */
  'paymentAmount',
  /** Stripe publishable key — public by design, but it SELECTS the Stripe account charged. */
  'stripePublicKey',
  /** Square location the hosted payment link is created against — routes the money. */
  'squareLocationId',
  /** Square live/sandbox mode — the difference between a real charge and a test one. */
  'squareEnv',
  /** Per-person deposit amounts + date overrides (the legacy deposit rail). */
  'depositConfig',
  /** The deposit rule engine: when a deposit is due, how much, hold vs charge, late-cancel fee. */
  'depositRules',
] as const);

export type YeboOnlyConfigKey = (typeof YEBO_ONLY_CONFIG_KEYS)[number];

/** Membership view of the same list, for O(1) lookups in render paths. */
export const YEBO_ONLY_CONFIG_KEY_SET: ReadonlySet<string> = new Set<string>(
  YEBO_ONLY_CONFIG_KEYS,
);

/**
 * The Yebo-only keys a config-write body touches, in list order.
 *
 * PRESENCE, not truthiness. `patchConfig` turns an explicit `undefined` into a
 * REMOVE and writes `null` verbatim, so `{ depositRules: undefined }` and
 * `{ depositRules: null }` both DESTROY a venue's deposit rules. A
 * `body[k] !== undefined` test — the idiom `assertNoServerOwnedKeys` uses for
 * keys that are only ever SET — would wave exactly those through.
 *
 * Own properties only: a prototype-polluted body must not be able to conjure a
 * refusal (or, in the read-only UI, a lock) out of `Object.prototype`.
 *
 * Pure and allocation-light: no I/O, and it returns `[]` for a primitive,
 * `null` or array body rather than throwing (`'k' in 123` is a TypeError, and
 * a guard that 500s is not a guard).
 */
export function yeboOnlyKeysIn(body: unknown): YeboOnlyConfigKey[] {
  if (typeof body !== 'object' || body === null || Array.isArray(body)) return [];
  return YEBO_ONLY_CONFIG_KEYS.filter((k) =>
    Object.prototype.hasOwnProperty.call(body, k),
  );
}
